import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { cn } from "@/utils/cn";
import { TransitionLink } from "@/components/ui/TransitionLink";
import { useContent } from "@/content/ContentContext";
import { isComingSoonFrame } from "@/data/availability";

const MENU_LINKS = [
  { href: "/about",    label: "About",    accent: false },
  { href: "/gallery",  label: "Gallery",  accent: false },
  { href: "/products", label: "Bikes",    accent: false },
  { href: "/builder",  label: "Build",    accent: true  },
  { href: "/contact",  label: "Contact",  accent: false },
];

// Model shortcuts shown under the Bikes menu entry
const MENU_BIKE_LINES = [
  { id: "felon-i",   mark: "Felon",   series: "I"   },
  { id: "raptor-i",  mark: "Raptor",  series: "I"   },
  { id: "typhoon-i", mark: "Typhoon", series: "I"   },
  { id: "raider",    mark: "Raider",  series: "E-I" },
];

const BRAND_WORDMARK = "/images/brand/burton-wordmark-white.png";
const BRAND_SHIELD = "/images/brand/burton-shield-gold.png";

export function MainLayout() {
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [isClosing,   setIsClosing]   = useState(false);
  const location = useLocation();
  const { content } = useContent();
  const { siteSettings } = content;

  const handleLogoClick = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Animated close — only for × button and Escape key
  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setMenuOpen(false);
      setIsClosing(false);
    }, 450);
  };

  // Instant close — for link clicks; the page transition overlay covers the screen
  const closeMenuInstant = () => {
    setMenuOpen(false);
    setIsClosing(false);
  };

  // Close menu after a page transition completes (pathname change only —
  // hash changes are handled by the animated closeMenu on hash links)
  useEffect(() => {
    closeMenuInstant();
  }, [location.pathname]);

  // Lock body scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Close on Escape with fade animation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-carbon-900">
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 inset-x-0 z-50">
        <div className="px-10 md:px-14 lg:px-16 xl:px-20 2xl:px-24 3xl:px-32 h-28 flex items-center justify-between">
          <TransitionLink
            to="/"
            onClick={handleLogoClick}
            className="hover:opacity-70 transition-opacity duration-200"
          >
            <img
              src={BRAND_SHIELD}
              alt="Burton Bikes"
              className="h-16 w-auto"
            />
          </TransitionLink>

          {/* Menu icon — two asymmetric lines, not a burger */}
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            className="text-gold-500 hover:opacity-70 transition-opacity duration-200 p-2 -mr-2"
          >
            <svg width="30" height="16" viewBox="0 0 30 16" fill="none">
              <line
                x1="0"
                y1="1.5"
                x2="30"
                y2="1.5"
                stroke="currentColor"
                strokeWidth="2"
              />
              <line
                x1="10"
                y1="14"
                x2="30"
                y2="14"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>
      </header>

      {/* ── Full-screen menu overlay ─────────────────────────────────────── */}
      {(menuOpen || isClosing) && (
        <div className={`fixed inset-0 z-[200] bg-carbon-900/95 backdrop-blur-xl flex flex-col ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}>
          {/* Top bar — mirrors nav height */}
          <div className="flex items-center justify-between px-10 md:px-14 lg:px-16 xl:px-20 2xl:px-24 3xl:px-32 h-28 shrink-0">
            <TransitionLink
              to="/"
              className="hover:opacity-70 transition-opacity duration-200"
            >
              <img
                src={BRAND_SHIELD}
                alt="Burton Bikes"
                className="h-18 w-auto"
              />
            </TransitionLink>

            {/* Close — × */}
            <button
              onClick={closeMenu}
              aria-label="Close menu"
              className="text-titanium-400 hover:text-carbon-50 transition-colors duration-150 p-2 -mr-2"
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                <line
                  x1="1"
                  y1="1"
                  x2="21"
                  y2="21"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <line
                  x1="21"
                  y1="1"
                  x2="1"
                  y2="21"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
            </button>
          </div>

          {/* Nav links */}
          <nav className="flex-1 flex flex-col justify-center px-10 md:px-14 lg:px-16 xl:px-20 2xl:px-24 3xl:px-32">
            {MENU_LINKS.map(({ href, label, accent }, i) => {
              const linkClass = cn(
                "group flex items-center justify-between border-b border-carbon-800",
                "font-display font-extrabold [font-stretch:125%] uppercase leading-none",
                "text-4xl md:text-5xl lg:text-6xl xl:text-7xl",
                "py-5 md:py-6 transition-colors duration-150",
                "animate-slide-up",
                accent
                  ? "text-gold-500 hover:text-gold-400"
                  : "text-carbon-50/50 hover:text-carbon-50",
              );
              const inner = (
                <>
                  {label}
                  <span
                    className={cn(
                      "transition-transform duration-150 group-hover:translate-x-2",
                      "text-xl md:text-2xl",
                      accent ? "text-gold-500" : "text-titanium-500",
                    )}
                  >
                    →
                  </span>
                </>
              );

              if (href === "/products") {
                return (
                  <div key={href} className="border-b border-carbon-800">
                    <TransitionLink
                      to={href}
                      style={{ animationDelay: `${i * 0.08 + 0.1}s` }}
                      className={cn(linkClass, "border-b-0")}
                    >
                      {inner}
                    </TransitionLink>
                    <div
                      style={{ animationDelay: `${i * 0.08 + 0.18}s` }}
                      className="flex flex-wrap gap-x-8 gap-y-2 pb-6 animate-slide-up"
                    >
                      {MENU_BIKE_LINES.map(({ id, mark, series }) => {
                        const comingSoon = isComingSoonFrame(id);
                        return (
                          <TransitionLink
                            key={id}
                            to={`/bikes/${id}`}
                            className="group/line flex items-baseline gap-1.5 font-display font-bold text-sm md:text-base tracking-wider uppercase text-titanium-500 hover:text-carbon-50 transition-colors duration-150"
                          >
                            {mark}
                            <span className="font-serif italic text-gold-600 group-hover/line:text-gold-500 transition-colors duration-150">
                              {series}
                            </span>
                            {comingSoon && (
                              <span className="ml-1 font-mono text-2xs tracking-widest text-gold-600 group-hover/line:text-gold-500 transition-colors duration-150">
                                Coming soon
                              </span>
                            )}
                          </TransitionLink>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              return href.includes("#") ? (
                <a
                  key={href}
                  href={href}
                  onClick={closeMenu}
                  style={{ animationDelay: `${i * 0.08 + 0.1}s` }}
                  className={linkClass}
                >
                  {inner}
                </a>
              ) : (
                <TransitionLink
                  key={href}
                  to={href}
                  style={{ animationDelay: `${i * 0.08 + 0.1}s` }}
                  className={linkClass}
                >
                  {inner}
                </TransitionLink>
              );
            })}
          </nav>

          {/* Overlay footer */}
          <div className="px-10 md:px-14 lg:px-16 xl:px-20 2xl:px-24 3xl:px-32 pb-10">
            <p className="font-mono text-xs text-titanium-700 tracking-[0.35em] uppercase">
              Cambridge, New Zealand · Custom geometry · Enquiry-based
            </p>
          </div>
        </div>
      )}

      {/* ── Page content ─────────────────────────────────────────────────── */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-carbon-800 bg-carbon-950">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-10">

          {/* Main footer grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 pb-12 border-b border-carbon-800">

            {/* Brand column */}
            <div className="md:col-span-1 flex flex-col gap-5">
              <Link to="/" className="hover:opacity-70 transition-opacity duration-200 self-start">
                <img src={BRAND_WORDMARK} alt="Burton Bikes" className="h-8 w-auto" />
              </Link>
              <p className="text-titanium-500 text-xs leading-relaxed max-w-xs">
                Bespoke titanium and carbon bicycles built to order in Cambridge, New Zealand. Every frame a collaboration.
              </p>
              <div className="flex flex-col gap-1">
                <a
                  href={`mailto:${siteSettings.contactEmail}`}
                  className="text-titanium-400 hover:text-gold-500 transition-colors duration-150 font-mono text-xs tracking-wider"
                >
                  {siteSettings.contactEmail}
                </a>
                <span className="text-titanium-600 font-mono text-xs">Cambridge, New Zealand</span>
              </div>
            </div>

            {/* Navigate */}
            <div className="flex flex-col gap-4">
              <p className="font-mono text-2xs tracking-[0.35em] uppercase text-gold-500">Navigate</p>
              <nav className="flex flex-col gap-3">
                {[
                  { href: '/',         label: 'Home' },
                  { href: '/about',    label: 'About' },
                  { href: '/gallery',  label: 'Gallery' },
                  { href: '/products', label: 'Bikes' },
                  { href: '/contact',  label: 'Contact' },
                ].map(({ href, label }) =>
                  href.includes('#') ? (
                    <a key={href} href={href} className="text-titanium-400 hover:text-carbon-50 transition-colors duration-150 font-display text-sm tracking-wider uppercase">
                      {label}
                    </a>
                  ) : (
                    <TransitionLink key={href} to={href} className="text-titanium-400 hover:text-carbon-50 transition-colors duration-150 font-display text-sm tracking-wider uppercase">
                      {label}
                    </TransitionLink>
                  )
                )}
              </nav>
            </div>

            {/* Bikes */}
            <div className="flex flex-col gap-4">
              <p className="font-mono text-2xs tracking-[0.35em] uppercase text-gold-500">Bike lines</p>
              <nav className="flex flex-col gap-3">
                {[
                  { label: 'Felon', series: 'I', desc: 'Coming soon / DH platform', id: 'felon-i' },
                  { label: 'Raptor', series: 'I', desc: 'Enduro platform', id: 'raptor-i' },
                  { label: 'Typhoon', series: 'I', desc: 'Coming soon / Down-country platform', id: 'typhoon-i' },
                  { label: 'Raider', series: 'E-I', desc: 'Coming soon / E-Enduro platform', id: 'raider' },
                ].map(({ label, series, desc, id }) => (
                  <TransitionLink key={label} to={`/bikes/${id}`} className="flex flex-col gap-0.5 group">
                    <span className="text-titanium-400 group-hover:text-carbon-50 transition-colors duration-150 font-display text-sm tracking-wider uppercase">
                      {label} <span className="text-gold-600 group-hover:text-gold-500 font-serif italic transition-colors duration-150">{series}</span>
                    </span>
                    <span className="text-titanium-700 font-mono text-2xs tracking-wider">{desc}</span>
                  </TransitionLink>
                ))}
              </nav>
            </div>

            {/* Build */}
            <div className="flex flex-col gap-4">
              <p className="font-mono text-2xs tracking-[0.35em] uppercase text-gold-500">Start a build</p>
              <p className="text-titanium-500 text-xs leading-relaxed">
                Every Burton bike is built to your geometry and riding style. Start the configurator to shape your frame, fit, and finish.
              </p>
              <TransitionLink
                to="/builder"
                className="self-start inline-flex items-center gap-3 border border-gold-500 text-carbon-50 font-display font-bold text-xs tracking-widest uppercase px-5 py-3 hover:bg-gold-500 hover:text-carbon-900 transition-all duration-300 ease-out-expo"
              >
                Open builder →
              </TransitionLink>
            </div>
          </div>

          {/* Legal / trust links */}
          <nav className="py-8 border-b border-carbon-800 flex flex-wrap gap-x-6 gap-y-3">
            {[
              { slug: 'terms',                     label: 'Terms & Conditions' },
              { slug: 'privacy',                   label: 'Privacy Policy' },
              { slug: 'refunds-returns-warranty',  label: 'Refunds, Returns & Warranty' },
              { slug: 'shipping-delivery',         label: 'Shipping & Delivery' },
              { slug: 'safety-intended-use',       label: 'Safety & Intended Use' },
              { slug: 'custom-order-terms',        label: 'Custom Order Terms' },
              { slug: 'cookies',                   label: 'Cookie & Tracking Notice' },
            ].map(({ slug, label }) => (
              <TransitionLink
                key={slug}
                to={`/legal/${slug}`}
                className="text-titanium-500 hover:text-gold-500 transition-colors duration-150 font-mono text-2xs tracking-widest uppercase"
              >
                {label}
              </TransitionLink>
            ))}
            <TransitionLink
              to="/contact"
              className="text-titanium-500 hover:text-gold-500 transition-colors duration-150 font-mono text-2xs tracking-widest uppercase"
            >
              Contact & Complaints
            </TransitionLink>
          </nav>

          {/* Bottom bar */}
          <div className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <p className="text-titanium-700 font-mono text-2xs tracking-widest uppercase">
              {new Date().getFullYear()} {siteSettings.footerText}
            </p>
            <a
              href="https://azzudo.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-titanium-700 hover:text-titanium-500 transition-colors duration-150 font-mono text-2xs tracking-widest uppercase"
            >
              Site by Azzudo
            </a>
          </div>

        </div>
      </footer>
    </div>
  );
}
